import { api } from "@/lib/apiClient";
import { formatMoneyDe, roundMoney } from "@/lib/artikelZuschlag";

export type RabattModus = "regel" | "manuell_prozent";

export type RabattRegel = {
  id?: number;
  ab_listenpreis_netto: number;
  rabatt_prozent: number;
  sort_index?: number;
};

export interface RabattStaffelRow {
  id: number;
  ab_listenpreis_netto: number;
  rabatt_prozent: number;
  sort_index: number;
  ek: number | null;
  is_match: boolean;
}

export interface ArtikelRabattState {
  modus: RabattModus | null;
  regel_id: number | null;
  manuell_prozent: number | null;
  matched_regel_id: number | null;
  staffeln: RabattStaffelRow[];
  listenpreis_netto: number | null;
  ek_netto: number | null;
  rabatt_prozent: number | null;
}

export async function getRabattRegeln(): Promise<RabattRegel[]> {
  const data = await api.get<{ regeln: RabattRegel[] }>("/artikel/einstellungen/rabatt-regeln");
  return Array.isArray(data?.regeln) ? data.regeln : [];
}

export async function saveRabattRegeln(regeln: RabattRegel[]): Promise<RabattRegel[]> {
  const data = await api.put<{ regeln: RabattRegel[] }>("/artikel/einstellungen/rabatt-regeln", {
    regeln: regeln.map((r, index) => ({
      ab_listenpreis_netto: Math.round((Number(r.ab_listenpreis_netto) || 0) * 100) / 100,
      rabatt_prozent: Math.round((Number(r.rabatt_prozent) || 0) * 100) / 100,
      sort_index: r.sort_index ?? index,
    })),
  });
  return Array.isArray(data?.regeln) ? data.regeln : [];
}

/** Höchste Staffel mit ab_listenpreis_netto <= Listenpreis. */
export function matchRabattRegel(
  listenpreis: number | null | undefined,
  regeln: RabattRegel[],
): RabattRegel | null {
  if (listenpreis == null || !Number.isFinite(Number(listenpreis))) return null;
  const lp = Number(listenpreis);
  let best: RabattRegel | null = null;
  for (const r of regeln) {
    const ab = Number(r.ab_listenpreis_netto) || 0;
    if (ab <= lp && (best == null || ab >= Number(best.ab_listenpreis_netto))) {
      best = r;
    }
  }
  return best;
}

/** EK = Listenpreis × (1 − Rabatt%/100) */
export function ekFromListenpreis(listenpreis: number, rabattProzent: number): number {
  const lp = Math.max(0, Number(listenpreis) || 0);
  const pct = Math.max(0, Number(rabattProzent) || 0);
  return Math.round(lp * (1 - pct / 100) * 100) / 100;
}

/** Rabatt % aus Listenpreis und EK: (1 − EK/Listenpreis) × 100. */
export function rabattFromListenpreisEk(
  listenpreis: number | null | undefined,
  ek: number | null | undefined,
): number | null {
  const lp = Number(listenpreis);
  const ekVal = Number(ek);
  if (!(lp > 0) || !Number.isFinite(ekVal)) return null;
  return Math.round((1 - ekVal / lp) * 10000) / 100;
}

export async function saveArtikelRabatt(
  artikelId: number,
  body: {
    modus: RabattModus;
    regel_id?: number | null;
    manuell_prozent?: number | null;
  },
): Promise<{
  ok: boolean;
  modus: RabattModus;
  regel_id: number | null;
  rabatt: number;
  ek_netto: number | null;
}> {
  return api.put(`/artikel/${artikelId}/rabatt`, body);
}

export function rabattStaffelLabel(
  rule: Pick<RabattRegel, "ab_listenpreis_netto" | "rabatt_prozent">,
): string {
  return `ab ${formatMoneyDe(rule.ab_listenpreis_netto)} € · −${formatMoneyDe(rule.rabatt_prozent)} %`;
}

export function rabattStateFromRegeln(
  regeln: RabattRegel[],
  listenpreis: number | null = null,
): ArtikelRabattState {
  const staffeln: RabattStaffelRow[] = regeln
    .filter((row): row is RabattRegel & { id: number } => row.id != null)
    .map((row) => ({
      id: row.id,
      ab_listenpreis_netto: row.ab_listenpreis_netto,
      rabatt_prozent: row.rabatt_prozent,
      sort_index: row.sort_index ?? 0,
      ek: listenpreis != null && listenpreis > 0 ? ekFromListenpreis(listenpreis, row.rabatt_prozent) : null,
      is_match: false,
    }));
  const matched = matchRabattRegel(listenpreis, regeln);
  return {
    modus: null,
    regel_id: null,
    manuell_prozent: null,
    matched_regel_id: matched?.id ?? null,
    staffeln,
    listenpreis_netto: listenpreis,
    ek_netto: null,
    rabatt_prozent: null,
  };
}

export { roundMoney };
